// Copyright (c) Docugami, Inc. All rights reserved.
import React, { useEffect, useState } from 'react';
import { fetchOrganizationList, createOrganization, pollCreateOrganizationStatus } from '../../../store/organization/actions';
import { NameOrganizationStep } from './CreateOrganizationSteps';
import { DgUnauthenticatedShell } from './Header';
import { useSelector } from 'react-redux';
import { AppState } from 'store';
import { useAppDispatch } from 'hooks/redux';
import { useDgAuth } from 'hooks/DgAuthentication';
import OrganizationWaitingRoom from './OrganizationWaitingRoom';
import { useOrgStatus } from 'hooks/OrgStatus';
import styled from 'styled-components';
import { getSelfServiceAccessCode, setSelfServiceAccessCode } from 'utils/localStorageHelpers';
import { useSetUserProfileMutation } from 'store/user/auth0UserSlice';
import * as queryString from 'query-string';
import { useLocation } from 'react-router';
import { NavigationType, useNavigationService } from 'hooks/navigationService';

const Root = styled.div`
    display: flex;
    justify-content: center;
    height: 100%;
    width: 100%;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    max-width: 600px;
    height: 100%;
    width: 100%;
`;

interface OrganizationInfo {
    name: string;
}

export const CreateOrganization = (): JSX.Element => {
    const { user, getAccessTokenSilently, isUserNamePassword } = useDgAuth();
    const { currentOrgInitializing, needToCreateAnOrg, waitlisted, unknown } = useOrgStatus();

    const currentOrganization = useSelector((state: AppState) => state.organizations.currentOrganization);
    const [organizationName, setOrganizationName] = useState(currentOrganization?.name);
    const [createOrganizationState, setCreateOrganizationState] = useState<OrganizationInfo>({ name: '' });
    const [firstName, setFirstName] = useState(user?.given_name);
    const [lastName, setLastName] = useState(user?.family_name);

    const organizationFetchState = useSelector((state: AppState) => state.organizations.organizationList.fetchState);
    const creationState = useSelector((state: AppState) => state.organizations.creationState);
    const [setUserProfileTrigger] = useSetUserProfileMutation();

    const dispatch = useAppDispatch();
    const location = useLocation();
    const navService = useNavigationService();

    useEffect(() => {
        const values = queryString.parse(location.search);
        if (!!values['accessCode']) {
            setSelfServiceAccessCode(values['accessCode'] as string);
        }
    }, [location.search])

    useEffect(() => {
        if (organizationFetchState.fetched === false && organizationFetchState.fetching === false) {
            dispatch(fetchOrganizationList(getAccessTokenSilently));
        }
    }, [organizationFetchState, dispatch, getAccessTokenSilently])

    // Poll org creation status while organization is being created
    useEffect(() => {
        if (creationState.created) {
            navService.navToHomePage(NavigationType.ReplaceCurrentPage);
            return;
        }

        if (creationState.creating && !!creationState.statusUrl) {
            dispatch(pollCreateOrganizationStatus(creationState.statusUrl, getAccessTokenSilently));
        }
    }, [creationState, dispatch, navService, getAccessTokenSilently])

    // We are currently fetching organizations. Nothing to render yet.
    if (!organizationFetchState.fetched) {
        return <div></div>
    }

    function advanceNameOrganization(organizationName: string) {
        setCreateOrganizationState({ ...createOrganizationState, name: organizationName });
        const accessCode = getSelfServiceAccessCode();
        dispatch(createOrganization(organizationName, firstName!, lastName!, accessCode ?? "", getAccessTokenSilently));
        if (isUserNamePassword && !!firstName && !!lastName) {
            setUserProfileTrigger({ given_name: firstName, family_name: lastName});
        }
    }

    if (currentOrgInitializing || waitlisted) {
        return <OrganizationWaitingRoom/>
    }

    // The user does not belong to an organization. Give them the option to create one.
    if (needToCreateAnOrg) {
        return (
            <DgUnauthenticatedShell>
                <Root>
                    <Content>
                        <NameOrganizationStep firstName={firstName ?? ""} lastName={lastName ?? ""} orgName={organizationName} email={user?.email ?? ""} setOrgName={setOrganizationName} setFirstName={setFirstName} setLastName={setLastName} canAdvance={!unknown && !!organizationName?.trim() && !!firstName?.trim() && !!lastName?.trim()} onAdvance={() => advanceNameOrganization(organizationName)} />
                    </Content>
                </Root>
            </DgUnauthenticatedShell>
        );
    }

    return (<div></div>);
}

export default CreateOrganization;
